import { urlExterna } from '../urls/external-url';

/**
 * Destino de un logo de sponsor, en un solo lugar.
 *
 * Lo usan la franja del pie y la página de Partners. La data no trae el nombre
 * de la marca —cada logo es sólo `url` + `action`—, así que el host es lo único
 * cierto que se puede anunciar.
 */

export interface SponsorImg {
  url?: string;
  action?: { open?: string; moviment?: string };
  /**
   * Peso visual que el admin le asigna al partner según su importancia.
   * `null` mientras nadie lo defina — y ése es el caso de casi todos hoy.
   */
  size?: string | null;
}

/** Los tres pesos que valida el backend. Fuera de eso, se trata como base. */
export type SponsorSize = 'small' | 'medium' | 'large';

/**
 * Peso visual del logo, normalizado.
 *
 * Un sponsor sin tamaño asignado —o con un valor que no reconocemos— se dibuja
 * en el tamaño base. Eso importa: en producción hoy **ninguno** tiene tamaño,
 * así que el camino por defecto tiene que dejar la franja exactamente como
 * está (ABC-403).
 */
export function sponsorSize(img: SponsorImg): SponsorSize {
  const size = img?.size;
  return size === 'small' || size === 'large' ? size : 'medium';
}

/**
 * URL del sponsor, o null si no tiene destino.
 *
 * En producción 6 de los 16 logos vienen con `open: 'none'`; antes igual se
 * pintaban como botón con hover y no llevaban a ninguna parte (ABC-374).
 */
export function sponsorUrl(img: SponsorImg): string | null {
  const { open, moviment } = img?.action ?? {};
  if (open !== 'browser' || !moviment) {
    return null;
  }
  // El destino lo tipea un admin y puede venir sin esquema. Hoy los 16 sponsors
  // de producción lo traen, pero el campo admite un valor que se resolvería como
  // ruta relativa del sitio — la misma clase que rompió 28 perfiles de artista
  // (ABC-440).
  return urlExterna(moviment);
}

/**
 * Dominio del sponsor, para nombrar el enlace.
 *
 * El `alt` repetía la categoría y un lector de pantalla leía "Colabora" seis
 * veces seguidas.
 */
export function sponsorHost(img: SponsorImg): string {
  const url = img?.action?.moviment;
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
